// Glass-i — Stage 6P-MED-FIX shared dropdown loader state helper.
//
// Centralises the loading / error / empty / data semantics for AntD
// `<Select>` dropdowns that pull their options from an async source.
//
// Before this helper landed (F-FD-7), every async-loaded Select on the
// Stage 6P form pages silently swallowed errors: a failed `client.get`
// call set the options array to `[]` and the user just saw a blank
// dropdown with no signal that the network had broken. The same blank
// state was indistinguishable from "loading", "we tried and it
// errored", and "the server returned zero rows". Operators reported
// this as "the dropdown is broken" with no useful follow-up.
//
// `buildDropdownStateProps` is the single source of truth for that
// distinction. It returns the minimal prop pair an AntD Select needs:
//
//   {
//     loading:         boolean,
//     notFoundContent: string | null   // null = let AntD render its
//                                      // own default empty state
//   }
//
// The four states are mutually exclusive and resolved in priority
// order:
//
//   1. loading === true                 → spinner + "Loading…" label
//   2. error truthy                     → red error label
//   3. data is an array of length 0     → "No results found" label
//   4. otherwise                        → passthrough (no override)
//
// The helper is intentionally pure and does NOT import React or AntD —
// callers spread the returned props onto the Select. We return plain
// strings for `notFoundContent` instead of pre-built JSX so this file
// can be tested under plain Node without a JSX transform, and so
// callers can wrap the string in a Spin / Icon if they want richer
// chrome locally (none of the three Stage 6P pages do today).
//
// Tested in dropdownLoaderState.test.js.

// ── Default copy ────────────────────────────────────────────────────
// Centralised so the three pages stay visually consistent. Callers can
// override per call via `options.labels`.
export const DEFAULT_LABELS = Object.freeze({
  loading: 'Loading…',
  error:   'Failed to load — try refresh',
  empty:   'No results found'
});


/**
 * Build the `loading` + `notFoundContent` props for an AntD Select
 * whose options come from an async loader.
 *
 * @param {Object}   state
 * @param {boolean} [state.loading]   true while the loader is in flight
 * @param {*}       [state.error]     non-falsy → render the error state
 * @param {Array}   [state.data]      the options array (may be empty)
 * @param {Object}  [options]
 * @param {Object}  [options.labels]  override the default copy
 *
 * @returns {{ loading: boolean, notFoundContent: (string|null) }}
 */
export function buildDropdownStateProps(state, options) {
  const s = state && typeof state === 'object' ? state : {};
  const opts = options && typeof options === 'object' ? options : {};
  const labels = Object.assign({}, DEFAULT_LABELS, opts.labels || {});

  // Priority 1 — loading wins over everything. If a refresh is in
  // flight even with stale options on screen, the dropdown should
  // surface that, not the previous error.
  if (s.loading === true) {
    return {
      loading:         true,
      notFoundContent: labels.loading
    };
  }

  // Priority 2 — error. We accept any truthy value (Error instance,
  // string, axios payload, plain boolean) so callers don't have to
  // normalise before calling.
  if (s.error) {
    return {
      loading:         false,
      notFoundContent: labels.error
    };
  }

  // Priority 3 — empty success. Only flagged when `data` is explicitly
  // an array of length zero; if the caller hasn't loaded yet (data is
  // null / undefined) we fall through to the passthrough so AntD's own
  // empty state shows.
  if (Array.isArray(s.data) && s.data.length === 0) {
    return {
      loading:         false,
      notFoundContent: labels.empty
    };
  }

  // Priority 4 — passthrough. Returning `null` for notFoundContent
  // tells AntD to keep its own default rendering (the framework still
  // shows "No data" via Empty when there are no options, but in the
  // happy-data path the prop is a no-op).
  return {
    loading:         false,
    notFoundContent: null
  };
}
